import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { createService, setServiceActive, updateService } from '@/api/services/servicesApi';
import type { Service, ServiceWritePayload } from '@/api/services/servicesApi.types';
import { SERVICES_QUERY_KEY } from '@/constants/queryKeys';

function useServicesInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate() {
    queryClient.invalidateQueries({ queryKey: SERVICES_QUERY_KEY });
  };
}

interface SaveServiceVariables {
  serviceId: string | null;
  payload: ServiceWritePayload;
}

export function useSaveServiceMutation(): UseMutationResult<
  Service,
  Error,
  SaveServiceVariables
> {
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: function save(variables: SaveServiceVariables) {
      if (variables.serviceId === null) {
        return createService(variables.payload);
      }
      return updateService(variables.serviceId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

interface SetServiceActiveVariables {
  serviceId: string;
  isActive: boolean;
}

export function useSetServiceActiveMutation(): UseMutationResult<
  Service,
  Error,
  SetServiceActiveVariables
> {
  const invalidate = useServicesInvalidation();
  return useMutation({
    mutationFn: function setActive(variables: SetServiceActiveVariables) {
      return setServiceActive(variables.serviceId, variables.isActive);
    },
    onSuccess: invalidate,
  });
}
